
import { Button } from "@/components/ui/button";
import type { FunnelData, Page, BlogPost, FormElement } from "./types";
import { colorSchemes } from "./constants";
import { cn } from "@/lib/utils";
import { BlockRenderer } from "./BlockComponents/BlockRenderer";
import { Plus } from "lucide-react";

interface PreviewContentProps {
  data: FunnelData;
  colors: typeof colorSchemes.default;
  onShowForm: () => void;
  isSubmitted: boolean;
  currentPage: Page;
  blogPosts?: BlogPost[];
  formElements?: FormElement[];
  onFormSubmit?: (formData: Record<string, string>) => void;
  isSubmitting?: boolean;
}

export const PreviewContent = ({ 
  data, 
  colors, 
  onShowForm, 
  isSubmitted, 
  currentPage,
  blogPosts = [],
  formElements = [],
  onFormSubmit,
  isSubmitting
}: PreviewContentProps) => {
  const currentAfterSubmitPage = data.pages.find(page => page.slug === data.afterSubmit.buttonUrl.replace('/', ''));
  const allBlogPosts = blogPosts?.length ? blogPosts : (data.blogPosts || []);

  // Empty state component
  const EmptyState = () => (
    <div className="w-full px-8 py-16">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
        <div className="max-w-sm mx-auto space-y-4">
          <div className="bg-gray-50 rounded-full w-12 h-12 mx-auto flex items-center justify-center">
            <Plus className="w-6 h-6 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-700">This page is empty</h3>
          <p className="text-sm text-gray-500">
            Start building your page by adding sections and blocks using the editor on the left.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex-1 flex flex-col items-center justify-center relative overflow-y-auto">
      <div 
        className="fixed inset-0 opacity-50 -z-10 transition-colors duration-500"
        style={{ background: colors.secondary }}
      />

      {isSubmitted ? (
        <>
          {currentAfterSubmitPage ? (
            <>
              {currentAfterSubmitPage.sections.length === 0 ? (
                <EmptyState />
              ) : (
                currentAfterSubmitPage.sections.map((section) => {
                  const sectionBackground = section.theme === "dark" ? colors.dark :
                                          section.theme === "primary" ? colors.primary :
                                          colors.light;
                  
                  const sectionStyles = section.background?.image ? {
                    backgroundImage: `url(${section.background.image})`,
                    backgroundSize: section.background.size,
                    backgroundPosition: section.background.position,
                    backgroundRepeat: section.background.repeat,
                    backgroundColor: section.background.repeat === 'no-repeat' ? sectionBackground : undefined,
                    color: section.theme === "dark" ? colors.light :
                          section.theme === "primary" ? colors.light :
                          colors.dark,
                  } : {
                    background: sectionBackground,
                    color: section.theme === "dark" ? colors.light :
                          section.theme === "primary" ? colors.light :
                          colors.dark,
                  };
          
                  return (
                    <div
                      key={section.id}
                      id={section.id}
                      className={cn(
                        "w-full relative transition-all duration-500 outline outline-2 outline-offset-2 outline-blue-400/50",
                        "backdrop-blur-sm"
                      )}
                      style={sectionStyles}
                    >
                      <div className="max-w-4xl mx-auto px-8 py-16">
                        <div 
                          className={cn(
                            "prose max-w-none",
                            {
                              'grid grid-cols-2': section.layout === '2-col',
                              'grid grid-cols-3': section.layout === '3-col',
                              'grid grid-cols-4': section.layout === '4-col',
                            },
                            section.layout !== '1-col' && 'gap-8',
                            section.alignment?.horizontal === 'center' && 'justify-items-center',
                            section.alignment?.horizontal === 'end' && 'justify-items-end',
                            section.alignment?.vertical === 'center' && 'items-center',
                            section.alignment?.vertical === 'end' && 'items-end',
                          )}
                          style={{
                            display: section.layout === '1-col' ? 'block' : 'grid',
                            alignItems: section.alignment?.vertical || 'start',
                            justifyItems: section.alignment?.horizontal || 'start',
                          }}
                        >
                          {section.blocks.map((block) => (
                            <div key={block.id} className="transition-all duration-300">
                              <BlockRenderer
                                block={block}
                                sectionTheme={section.theme}
                                colors={colors}
                                onShowForm={onShowForm}
                                blogPosts={allBlogPosts}
                                formElements={formElements}
                                onFormSubmit={onFormSubmit}
                                isSubmitting={isSubmitting}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </>
          ) : (
            <div 
              className={cn(
                "w-full relative transition-all duration-500",
                "backdrop-blur-sm"
              )}
              style={{ 
                background: colors.light
              }}
            >
              <div className="max-w-4xl mx-auto px-8 py-16">
                <h2 
                  className="text-4xl font-bold mb-12 text-center"
                  style={{ color: colors.dark }}
                >
                  {data.afterSubmit.title}
                </h2>
                <p 
                  className="mb-6 leading-relaxed text-lg"
                  style={{ color: colors.dark }}
                >
                  {data.afterSubmit.text}
                </p>
                <Button
                  asChild
                  className="mb-6 transform hover:scale-105 transition-all duration-300 shadow-lg"
                  style={{ background: colors.primary, color: colors.light}}
                >
                  <a href={data.afterSubmit.buttonUrl}>{data.afterSubmit.buttonText}</a>
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {currentPage.sections.length === 0 ? (
            <EmptyState />
          ) : (
            currentPage.sections.map((section) => {
              const sectionBackground = section.theme === "dark" ? colors.dark :
                                      section.theme === "primary" ? colors.primary :
                                      colors.light;
              
              const sectionStyles = section.background?.image ? {
                backgroundImage: `url(${section.background.image})`,
                backgroundSize: section.background.size,
                backgroundPosition: section.background.position,
                backgroundRepeat: section.background.repeat,
                backgroundColor: section.background.repeat === 'no-repeat' ? sectionBackground : undefined,
                color: section.theme === "dark" ? colors.light :
                      section.theme === "primary" ? colors.light :
                      colors.dark,
              } : {
                background: sectionBackground,
                color: section.theme === "dark" ? colors.light :
                      section.theme === "primary" ? colors.light :
                      colors.dark,
              };
      
              return (
                <div
                  key={section.id}
                  id={section.id}
                  className={cn(
                    "w-full relative transition-all duration-500 outline outline-2 outline-offset-2 outline-blue-400/50",
                    "backdrop-blur-sm"
                  )}
                  style={sectionStyles}
                >
                  <div className="max-w-4xl mx-auto px-8 py-16">
                    <div 
                      className={cn(
                        "prose max-w-none",
                        {
                          'grid grid-cols-2': section.layout === '2-col',
                          'grid grid-cols-3': section.layout === '3-col',
                          'grid grid-cols-4': section.layout === '4-col',
                        },
                        section.layout !== '1-col' && 'gap-8',
                        section.alignment?.horizontal === 'center' && 'justify-items-center',
                        section.alignment?.horizontal === 'end' && 'justify-items-end',
                        section.alignment?.vertical === 'center' && 'items-center',
                        section.alignment?.vertical === 'end' && 'items-end',
                      )}
                      style={{
                        display: section.layout === '1-col' ? 'block' : 'grid',
                        alignItems: section.alignment?.vertical || 'start',
                        justifyItems: section.alignment?.horizontal || 'start',
                      }}
                    >
                      {section.blocks.map((block) => (
                        <div key={block.id} className="transition-all duration-300">
                          <BlockRenderer
                            block={block}
                            sectionTheme={section.theme}
                            colors={colors}
                            onShowForm={onShowForm}
                            blogPosts={allBlogPosts}
                            formElements={formElements}
                            onFormSubmit={onFormSubmit}
                            isSubmitting={isSubmitting}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </>
      )}
    </div>
  );
};
